import React, { useRef, useState } from 'react';
import { useDispatch } from 'src/state/redux';
import { CustomDeckGroup, PageSettingOverride } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNAModal,
  DNABox,
  DNAButton,
  DNAText,
  GenericToast,
  ToastOrientations,
  useDisableSwipe,
  useToast,
  Tabs,
  Iffy,
} from '@alucio/lux-ui';
import { DNAModalVariant } from 'src/components/DNA/Modal/DNAConnectedModal';
import DNAFolderNestedList,
{ DEFAULT_SHARED_FOLDER_FILTER, FolderSelectedMap } from 'src/components/DNA/Folder/NestedList/DNAFolderNestedList';
import DNAFolderUpsertModal from 'src/components/DNA/Modal/DNAFolderUpsert';
import { folderActions } from 'src/state/redux/slice/folder';
import { sharedFolderActions } from 'src/state/redux/slice/sharedFolder';
import * as guards from 'src/types/typeguards';
import DNAConditionalButton from '../DNA/ConditionalButton/DNAConditionalButton';
import { TABS_FOLDERS } from '../DNA/Modal/DNADocumentAddToFolder';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import type { FolderORM } from 'src/types/orms';
import { useAllFoldersInstance } from 'src/state/redux/selector/folder';

export type SelectFolderPresentationBuilderProps = {
  itemORM: CustomDeckGroup[]
  pageSettings?: PageSettingOverride[]
  title: string,
  onDone: () => void
  onSaveToFolder?: (folder: FolderORM) => Promise<void>
  personalFoldersOnly?: boolean
}

export const SelectFolderPresentationBuilder: DNAModalVariant<SelectFolderPresentationBuilderProps> = (props) => {
  const {
    itemORM, pageSettings, toggleModal, pushModal, closeModal, title, onDone,
    onSaveToFolder, personalFoldersOnly,
  } = props
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const completedFoldersRef = useRef(new Set<string>());
  const [saveError, setSaveError] = useState<string>();
  const [selected, setSelected] = useState<FolderSelectedMap>({})
  const [createdFolderId, setCreatedFolderId] = useState<string>();
  const toast = useToast();
  const [selectedTab, setSelectedTab] = useState(TABS_FOLDERS[0].title)
  const enableEditSharedFolder = useFeatureFlags('enableEditSharedFolder')
  const sharedFolders =  useAllFoldersInstance(DEFAULT_SHARED_FOLDER_FILTER, true)
  const isAnyRootFolderShared = sharedFolders.some(folder => !folder.relations.parentFolderORM)
  const tabs = !personalFoldersOnly && enableEditSharedFolder && isAnyRootFolderShared
    ? TABS_FOLDERS : [TABS_FOLDERS[0]]

  const dispatch = useDispatch()
  useDisableSwipe()

  const targetFolders = Object
    .values(selected)
    .filter(selection => selection && !selection.locked)
    .map(selection => selection?.folder).filter(guards.isFolderORM)

  const onCreateCustomDeck = async () => {
    if (!targetFolders.length || savingRef.current) return;
    if (onSaveToFolder) {
      savingRef.current = true;
      setIsSaving(true);
      setSaveError(undefined);
      try {
        for (const folder of targetFolders) {
          if (completedFoldersRef.current.has(folder.model.id)) continue;
          await onSaveToFolder(folder);
          completedFoldersRef.current.add(folder.model.id);
        }
        toggleModal();
        onDone();
      } catch {
        setSaveError('Could not finish saving. Retry to complete the remaining folders; saved copies will be kept.');
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
      return;
    }
    if (targetFolders.length) {
      const isSharedFolder = selectedTab === 'Shared With Me'
      targetFolders.forEach(folder => {
        if (isSharedFolder) {
          dispatch(sharedFolderActions.createCustomDeckInFolder({
            targetFolder: folder,
            selectedSlides: itemORM,
            pageSettings,
            title,
          },
          ))
        }
        else {
          dispatch(folderActions.createCustomDeck(
            folder,
            itemORM,
            title,
            undefined,
            pageSettings,
          ))
        }
        toast?.add(
          <GenericToast
            title="Presentation created"
            status="success"
          />,
          ToastOrientations.TOP_RIGHT,
        )
      })
      toggleModal()
      onDone()
    }
  }

  const onCreateFolder = () => {
    pushModal((props) => (<DNAFolderUpsertModal {...props} toast={toast} onCreated={setCreatedFolderId} />))
  }

  const onSelectedTab = (tab: string) => setSelectedTab(tab)
  const showSharedFolders = selectedTab === TABS_FOLDERS[1].title

  return (
    <DNAModal childFill={1}>
      <DNAModal.Header onClose={() => { if (!savingRef.current) closeModal(); }}>
        <DNABox spacing="sm" style={{ paddingTop: 12, paddingLeft: 16 }} >
          <DNAText h5>Add to Folders </DNAText>
          <DNAText status="subtle">{targetFolders.length} selected</DNAText>
        </DNABox>
      </DNAModal.Header>
      <DNAModal.Body>
        <DNABox appearance="col" fill>
          <Tabs
            style={{ paddingLeft: 0 }}
            selectedTab={selectedTab}
            selectedTabVariant="primary"
            tabs={tabs}
            childFill
            onSelectTab={onSelectedTab}
            styleTab={ tabs.length === 1 && { justifyContent: 'flex-start' }}
          />
          <DNAFolderNestedList
            createdFolderId={createdFolderId}
            onFolderSelect={selection => { if (!savingRef.current) setSelected(selection); }}
            showSharedFolders={selectedTab === 'Shared With Me'}
          />
        </DNABox>
      </DNAModal.Body>
      {saveError && <DNAText status="danger" style={{ padding: 16 }}>{saveError}</DNAText>}
      <DNAModal.Confirmation>
        <Iffy is={!showSharedFolders}>
          <DNAButton
            disabled={isSaving}
            onPress={onCreateFolder}
            appearance="outline"
          >Create new folder</DNAButton>
        </Iffy>
        <DNAConditionalButton
          onPress={onCreateCustomDeck}
        >
          <DNAConditionalButton.Disable condition={!targetFolders.length || isSaving}>
            <DNAText colorMode="dark">Select destination folder(s)</DNAText>
          </DNAConditionalButton.Disable>
          Done
        </DNAConditionalButton>
      </DNAModal.Confirmation>
    </DNAModal>
  )
}

export default SelectFolderPresentationBuilder
